import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../components/LandingLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CheckoutPage() {
  const { items, cartTotal } = useCart();
  const navigate = useNavigate();
  const [shippingMethod, setShippingMethod] = React.useState("delivery");
  const [shippingFee, setShippingFee] = React.useState(5);
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    county: "",
    eircode: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => setShippingFee(res.data.shipping_fee ?? 5))
      .catch(() => {});
  }, []);

  const shippingCost = shippingMethod === "delivery" ? shippingFee : 0;
  const grandTotal = cartTotal + shippingCost;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        shipping_method: shippingMethod,
        customer: form,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Unable to start checkout. Please try again.",
      );
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pb-12" data-testid="checkout-empty">
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900">
          Checkout
        </h1>
        <p className="mt-3 text-sm text-neutral-600">
          Your cart is empty.{" "}
          <button
            type="button"
            onClick={() => navigate("/shop")}
            className="underline underline-offset-4 hover:text-neutral-900"
            data-testid="checkout-go-shop"
          >
            Browse the shop
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="pb-16" data-testid="checkout-page">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Checkout
        </p>
        <h1 className="text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
          Complete your order
        </h1>
      </header>

      <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-start">
        <form onSubmit={handleSubmit} className="space-y-6 text-sm" data-testid="checkout-form">
          <div>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
              Delivery method
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { key: "delivery", label: "Postal delivery", note: `Ireland · €${Number(shippingFee).toFixed(2)}` },
                { key: "collection", label: "Collection", note: "Milk Market Lane, Tralee · Free" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setShippingMethod(opt.key)}
                  data-testid={`shipping-${opt.key}`}
                  className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                    shippingMethod === opt.key
                      ? "border-neutral-900 bg-white"
                      : "border-neutral-200 bg-white/60 hover:border-neutral-400"
                  }`}
                >
                  <p className="font-medium text-neutral-900">{opt.label}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">{opt.note}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name" required value={form.name} onChange={update("name")} testid="cust-name" />
            <Field label="Email" type="email" required value={form.email} onChange={update("email")} testid="cust-email" />
            <Field label="Phone" value={form.phone} onChange={update("phone")} testid="cust-phone" />
          </div>

          {shippingMethod === "delivery" && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="delivery-fields">
              <div className="md:col-span-2">
                <Field label="Address" required value={form.address} onChange={update("address")} testid="cust-address" />
              </div>
              <Field label="Town / City" required value={form.city} onChange={update("city")} testid="cust-city" />
              <Field label="County" required value={form.county} onChange={update("county")} testid="cust-county" />
              <Field label="Eircode" value={form.eircode} onChange={update("eircode")} testid="cust-eircode" />
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600" data-testid="checkout-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-submit"
            className="inline-flex h-11 w-full items-center justify-center rounded-full bg-black px-10 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-neutral-300"
          >
            {submitting ? "Redirecting to payment…" : `Pay €${grandTotal.toFixed(2)}`}
          </button>
          <p className="text-center text-[11px] text-neutral-400">
            Secure card payment powered by Stripe.
          </p>
        </form>

        <aside className="rounded-3xl border border-neutral-200 bg-white p-5 text-sm" data-testid="order-summary">
          <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-neutral-500">
            Order summary
          </h2>
          <ul className="mt-4 space-y-3">
            {items.map((item) => (
              <li key={item.id} className="flex justify-between gap-3">
                <span className="text-neutral-700">
                  {item.name} × {item.quantity}
                </span>
                <span className="text-neutral-900">
                  €{(item.price * item.quantity).toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-4 space-y-2 border-t border-neutral-100 pt-4">
            <Row label="Subtotal" value={`€${cartTotal.toFixed(2)}`} />
            <Row
              label={shippingMethod === "delivery" ? "Delivery" : "Collection"}
              value={shippingCost > 0 ? `€${shippingCost.toFixed(2)}` : "Free"}
            />
            <div className="flex justify-between pt-2 text-base font-medium text-neutral-900">
              <span>Total</span>
              <span data-testid="summary-total">€{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, type = "text", required, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
        {label} {required && <span className="text-neutral-400">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="h-10 w-full rounded-full border border-neutral-300 bg-white px-4 text-sm outline-none transition-colors focus:border-neutral-900"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-neutral-600">
      <span>{label}</span>
      <span className="text-neutral-900">{value}</span>
    </div>
  );
}
